/**
 * identity-service staff auth (T01-02/03/11) behind the admin `AuthApi` (T11-02):
 *
 * - `POST /v1/auth/staff/login` (mode "cookie") → tokens | mfa_required | enroll_required |
 *   password_change_required
 * - `POST /v1/auth/staff/mfa/verify`, `/mfa/enroll`, `/mfa/enroll/verify`, `/password/change`,
 *   `/password/forgot`, `/password/reset`
 * - `POST /v1/auth/staff/invite/check`, `/invite/accept` — the invitation e-mail's link (T01-18)
 * - `POST /v1/auth/token/refresh` + `POST /v1/auth/logout` — cookie authenticated, double-submit
 *   CSRF: the readable `staff_csrf` cookie echoed in `X-CSRF-Token`
 * - `GET /v1/auth/sessions`, `DELETE /v1/auth/sessions` (sign out everywhere else)
 *
 * The access token is kept in memory only (never in storage). Refreshes are serialised across tabs
 * with the Web Locks API: identity-service rotates the refresh cookie and treats a second use of
 * the same cookie as theft (whole family revoked), so two tabs must never refresh at once.
 */
import { createIdentityClient, type IdentityPaths } from "@tcg/api-client";

import {
  AuthError,
  type AuthApi,
  type LoginStep,
  normalizePermissions,
  type Session,
  type StaffSessionInfo,
} from "@/auth/types";
import { ApiError, apiErrorFrom, type FetchResultLike } from "@/lib/api/errors";

type Schemas =
  IdentityPaths["/v1/auth/staff/login"]["post"]["responses"][200]["content"]["application/json"];
type LoginOut = Schemas;
type TokenOut = NonNullable<LoginOut["tokens"]>;

export const STAFF_CSRF_COOKIE = "staff_csrf";
const REFRESH_LOCK = "tcg-admin-auth-refresh";

export interface IdentityAuthOptions {
  /** Gateway root, e.g. `/api` (paths are `/v1/...`). */
  baseUrl: string;
  fetch?: typeof fetch;
  getLocale?: () => string | undefined;
  /** `document.cookie` reader (tests inject one). */
  readCookie?: (name: string) => string | null;
}

export function readDocumentCookie(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  for (const part of document.cookie.split(";")) {
    const [key, ...rest] = part.trim().split("=");
    if (key === name) {
      return decodeURIComponent(rest.join("="));
    }
  }
  return null;
}

function expiry(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const at = Date.parse(value);
  return Number.isNaN(at) ? null : at;
}

export function sessionFromTokens(tokens: TokenOut): Session {
  const staff = tokens.staff;
  return {
    accessToken: tokens.accessToken,
    expiresAt: expiry(tokens.expiresAt) ?? Date.now() + tokens.expiresIn * 1000,
    sessionId: tokens.sessionId,
    user: {
      id: staff.id,
      displayName: staff.name || staff.email,
      email: staff.email,
      roles: [staff.role],
      permissions: normalizePermissions(staff.permissions),
      locale: staff.locale,
      totpEnabled: staff.totpEnabled,
      locationCodes: staff.allowedLocationCodes ?? [],
    },
  };
}

export function loginStepFrom(out: LoginOut): LoginStep {
  const expiresAt = expiry(out.challengeExpiresAt);
  if (out.step === "tokens" && out.tokens) {
    return { kind: "session", session: sessionFromTokens(out.tokens) };
  }
  if (out.step === "mfa_required" && out.mfaToken) {
    return { kind: "mfa", mfaToken: out.mfaToken, expiresAt };
  }
  if (out.step === "enroll_required" && out.enrollToken) {
    return { kind: "enroll", enrollToken: out.enrollToken, expiresAt };
  }
  if (out.step === "password_change_required" && out.changeToken) {
    return { kind: "password_change", changeToken: out.changeToken, expiresAt };
  }
  throw new AuthError("unknown");
}

/** identity error codes → the login screens' plain-language messages. */
export function authErrorFrom(error: ApiError): AuthError {
  switch (error.code) {
    case "invalid_credentials":
    case "unauthorized":
      return new AuthError("invalid");
    case "account_locked":
      return new AuthError("locked", error.retryAfter);
    case "rate_limited":
      return new AuthError("rate_limited", error.retryAfter);
    case "mfa_code_invalid":
    case "otp_code_invalid":
      return new AuthError("code_invalid");
    case "challenge_token_invalid":
      return new AuthError("challenge_expired");
    case "password_too_weak":
    case "validation_failed":
      return new AuthError("weak_password");
    case "network_error":
      return new AuthError("network");
    default:
      return error.status === 0 ? new AuthError("network") : new AuthError("unknown");
  }
}

async function call<T>(request: Promise<FetchResultLike<T>>): Promise<T> {
  let result: FetchResultLike<T>;
  try {
    result = await request;
  } catch {
    throw new AuthError("network");
  }
  if (!result.response.ok) {
    throw authErrorFrom(apiErrorFrom(result.response, result.error));
  }
  return result.data as T;
}

async function withRefreshLock<T>(run: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
  if (!locks) {
    return run();
  }
  return locks.request(REFRESH_LOCK, { mode: "exclusive" }, run);
}

export function createIdentityAuthApi(options: IdentityAuthOptions): AuthApi {
  const readCookie = options.readCookie ?? readDocumentCookie;
  let csrfFromBody: string | null = null;
  const client = createIdentityClient({
    baseUrl: options.baseUrl,
    getLocale: options.getLocale,
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });
  const bearer = (session: Session | null | undefined) =>
    session ? { Authorization: `Bearer ${session.accessToken}` } : undefined;
  const csrfHeader = (): Record<string, string> => {
    const token = readCookie(STAFF_CSRF_COOKIE) ?? csrfFromBody;
    return token ? { "X-CSRF-Token": token } : {};
  };
  const remember = (tokens: TokenOut | null | undefined) => {
    if (tokens?.csrfToken) {
      csrfFromBody = tokens.csrfToken;
    }
  };
  const step = (out: LoginOut): LoginStep => {
    remember(out.tokens);
    return loginStepFrom(out);
  };

  return {
    persistSession: false,

    async signIn({ email, password }) {
      return step(
        await call(
          client.POST("/v1/auth/staff/login", {
            body: { email: email.trim(), password, mode: "cookie" },
          }),
        ),
      );
    },

    async verifyMfa(mfaToken, code) {
      const tokens = await call(
        client.POST("/v1/auth/staff/mfa/verify", { body: { mfaToken, code: code.trim() } }),
      );
      remember(tokens);
      return sessionFromTokens(tokens);
    },

    async startEnrollment(enrollToken, session) {
      const out = await call(
        client.POST("/v1/auth/staff/mfa/enroll", {
          body: enrollToken ? { enrollToken } : {},
          headers: bearer(enrollToken ? null : session),
        }),
      );
      return { secret: out.secret, otpauthUri: out.otpauthUri, qrSvg: out.qrSvg };
    },

    async confirmEnrollment(enrollToken, code, session) {
      const out = await call(
        client.POST("/v1/auth/staff/mfa/enroll/verify", {
          body: { ...(enrollToken ? { enrollToken } : {}), code: code.trim() },
          headers: bearer(enrollToken ? null : session),
        }),
      );
      remember(out.tokens);
      return {
        recoveryCodes: out.recoveryCodes,
        session: out.tokens ? sessionFromTokens(out.tokens) : null,
      };
    },

    async changePassword({ currentPassword, newPassword, changeToken }, session) {
      const out = await call(
        client.POST("/v1/auth/staff/password/change", {
          body: {
            currentPassword,
            newPassword,
            mode: "cookie",
            ...(changeToken ? { changeToken } : {}),
          },
          headers: bearer(changeToken ? null : session),
        }),
      );
      return out.next ? step(out.next) : null;
    },

    async forgotPassword(email) {
      await call(client.POST("/v1/auth/staff/password/forgot", { body: { email: email.trim() } }));
    },

    async resetPassword(token, newPassword) {
      await call(client.POST("/v1/auth/staff/password/reset", { body: { token, newPassword } }));
    },

    async checkInvite(token) {
      const out = await call(client.POST("/v1/auth/staff/invite/check", { body: { token } }));
      return {
        email: out.email,
        name: out.name,
        expiresAt: expiry(out.expiresAt),
        requiresTotp: out.requiresTotp,
      };
    },

    async acceptInvite(token, newPassword) {
      return step(
        await call(
          client.POST("/v1/auth/staff/invite/accept", {
            body: { token, newPassword, mode: "cookie" },
          }),
        ),
      );
    },

    refresh() {
      return withRefreshLock(async () => {
        const csrf = csrfHeader();
        if (!csrf["X-CSRF-Token"]) {
          return null; // never signed in on this browser (no cookie pair)
        }
        try {
          const { data, response } = await client.POST("/v1/auth/token/refresh", {
            headers: csrf,
          });
          if (!response.ok || !data) {
            return null;
          }
          remember(data);
          return sessionFromTokens(data);
        } catch {
          return null;
        }
      });
    },

    async signOut(session) {
      try {
        await client.POST("/v1/auth/logout", { headers: { ...bearer(session), ...csrfHeader() } });
      } finally {
        csrfFromBody = null;
      }
    },

    async listSessions(session) {
      const out = await call(client.GET("/v1/auth/sessions", { headers: bearer(session) }));
      return out.data
        .filter((row) => row.kind === "staff")
        .map((row): StaffSessionInfo => ({
          id: row.id,
          userAgent: row.userAgent ?? null,
          ip: row.ip ?? null,
          createdAt: row.createdAt,
          lastUsedAt: row.lastUsedAt ?? null,
          current: row.current,
        }));
    },

    async revokeOtherSessions(session) {
      const out = await call(client.DELETE("/v1/auth/sessions", { headers: bearer(session) }));
      return out.revoked;
    },
  };
}

export { ApiError };
